import { FmsNotificationInboxItem, FmsNotificationPayload, useFmsNotifications } from 'hooks/useFmsNotifications'
import { useNotificationSandwichGateController } from 'hooks/useNotificationSandwichGate'
import { useNotificationSnooze } from 'hooks/useNotificationSnooze'
import { type ReactNode, createContext, useContext, useMemo, useRef } from 'react'

type FmsNotificationsContextValue = ReturnType<typeof useFmsNotifications> &
  ReturnType<typeof useNotificationSnooze> &
  ReturnType<typeof useNotificationSandwichGateController>

const FmsNotificationsContext = createContext<FmsNotificationsContextValue | null>(null)

export function FmsNotificationsProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const gate = useNotificationSandwichGateController()
  const snooze = useNotificationSnooze(enabled)
  const suppressSandwichAutoOpen = gate.suppressSandwichAutoOpen || snooze.isSnoozed || snooze.isQuietHours
  const suppressToastRef = useRef(false)
  suppressToastRef.current = suppressSandwichAutoOpen
  const notifications = useFmsNotifications(enabled, { suppressToastRef })
  const value = useMemo(
    () => ({
      ...notifications,
      ...snooze,
      ...gate,
      suppressSandwichAutoOpen,
      sandwichSuppressReasons: [
        ...gate.sandwichSuppressReasons,
        ...(snooze.isSnoozed ? ['snooze'] : []),
        ...(snooze.isQuietHours ? ['quiet-hours'] : []),
      ],
    }),
    [notifications, snooze, gate, suppressSandwichAutoOpen]
  )
  return <FmsNotificationsContext.Provider value={value}>{children}</FmsNotificationsContext.Provider>
}

export function useFmsNotificationsContext(): FmsNotificationsContextValue {
  const ctx = useContext(FmsNotificationsContext)
  if (!ctx) {
    throw new Error('useFmsNotificationsContext requires FmsNotificationsProvider')
  }
  return ctx
}

export function useFmsNotificationsContextIfPresent(): FmsNotificationsContextValue | null {
  return useContext(FmsNotificationsContext)
}

export type { FmsNotificationInboxItem, FmsNotificationPayload }
